import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { confirmDialog, promptDialog } from "@/lib/confirmDialog";
import {
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Trash2,
  Info,
} from "lucide-react";

/**
 * DbHealthDialog — admin-only DB sanity audit.
 *
 *  • Fetches `/api/admin/db-health` which scans every business table
 *    in `TABLE_REGISTRY` and reports rows that fail the required-field
 *    check (e.g. orphan test data left behind by the testing-agent or
 *    a partial import).
 *  • Per-table «Καθαρισμός» button calls
 *    `POST /api/admin/db-health/cleanup` with the admin-code to
 *    physically delete the malformed rows. Admin-code is requested
 *    once and persisted in the dialog state for the session.
 */
export default function DbHealthDialog({ open, onClose }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adminCode, setAdminCode] = useState("");
  const [cleaningTable, setCleaningTable] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await http.get("/admin/db-health");
      setReport(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία ελέγχου");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) refresh(); }, [open]);

  const cleanupTable = async (t) => {
    if (!adminCode.trim()) {
      toast.error("Συμπληρώστε τον κωδικό διαχειριστή");
      return;
    }
    if (!(await confirmDialog(`Διαγραφή ${t.bad_count} κατεστραμμένων εγγραφών από «${t.label}»; (Δεν υπάρχει undo)`))) {
      return;
    }
    setCleaningTable(t.name);
    try {
      const r = await http.post("/admin/db-health/cleanup", { admin_code: adminCode, table: t.name });
      toast.success(`Διαγράφηκαν ${r.data.deleted} εγγραφές από ${t.label}`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Αποτυχία καθαρισμού");
    } finally {
      setCleaningTable(null);
    }
  };

  const summary = report?.summary || { total_bad: 0, tables_with_issues: 0 };
  const tables = report?.tables || [];
  const bad = tables.filter((t) => t.bad_count > 0);
  const ok = tables.filter((t) => t.checked && t.bad_count === 0);
  const info = tables.filter((t) => !t.checked);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-5 py-3 border-b border-slate-200 bg-slate-50">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <ShieldCheck size={20} /> Έλεγχος Υγείας Δεδομένων
          </DialogTitle>
          <DialogDescription className="text-xs">
            Σαρώνει όλους τους πίνακες και εντοπίζει εγγραφές με ελλιπή υποχρεωτικά πεδία.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-2">
            <Card className={`p-3 ${summary.total_bad === 0 ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
              <div className="text-[10px] uppercase font-bold text-slate-600 tracking-wide">Προβληματικές</div>
              <div className={`text-3xl font-bold font-mono ${summary.total_bad === 0 ? "text-emerald-700" : "text-red-700"}`} data-testid="db-health-total-bad">
                {summary.total_bad}
              </div>
            </Card>
            <Card className="p-3 bg-amber-50 border-amber-200">
              <div className="text-[10px] uppercase font-bold text-slate-600 tracking-wide">Πίνακες με Σφάλματα</div>
              <div className="text-3xl font-bold font-mono text-amber-700" data-testid="db-health-tables-with-issues">
                {summary.tables_with_issues}
              </div>
            </Card>
            <Card className="p-3 bg-blue-50 border-blue-200">
              <div className="text-[10px] uppercase font-bold text-slate-600 tracking-wide">Σύνολο Πινάκων</div>
              <div className="text-3xl font-bold font-mono text-blue-700">{tables.length}</div>
            </Card>
          </div>

          {/* Admin code (only relevant if there's something to clean) */}
          {bad.length > 0 && (
            <Card className="p-3 bg-amber-50/40 border-amber-200">
              <Label className="text-xs font-bold">Κωδικός Διαχειριστή (για καθαρισμό)</Label>
              <Input
                type="password"
                value={adminCode}
                onChange={(e) => setAdminCode(e.target.value)}
                placeholder="••••"
                className="font-mono h-9 mt-1 max-w-xs"
                data-testid="db-health-admin-code"
              />
            </Card>
          )}

          {/* Bad tables */}
          {loading ? (
            <div className="text-center py-6 text-slate-400 flex items-center justify-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Φόρτωση...
            </div>
          ) : (
            <>
              {bad.length > 0 && (
                <div>
                  <div className="text-xs font-bold uppercase text-red-700 mb-1.5 flex items-center gap-1">
                    <AlertTriangle size={13} /> Πίνακες με Προβλήματα
                  </div>
                  <div className="space-y-2">
                    {bad.map((t) => (
                      <Card key={t.name} className="p-3 border-2 border-red-200 bg-red-50/30" data-testid={`db-health-bad-${t.name}`}>
                        <div className="flex items-center justify-between mb-1.5">
                          <div>
                            <div className="font-bold">{t.label}</div>
                            <div className="text-[10px] text-slate-500 font-mono">
                              {t.count} συνολικά · απαιτούμενα: {t.required.join(", ")}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded text-sm">
                              {t.bad_count} προβληματικές
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-700 border-red-300 hover:bg-red-100 h-8"
                              onClick={() => cleanupTable(t)}
                              disabled={cleaningTable === t.name || !adminCode.trim()}
                              data-testid={`db-health-cleanup-${t.name}`}
                            >
                              {cleaningTable === t.name
                                ? <Loader2 size={12} className="animate-spin" />
                                : <Trash2 size={12} className="mr-1" />}
                              Καθαρισμός
                            </Button>
                          </div>
                        </div>
                        {t.bad_rows.length > 0 && (
                          <div className="text-[10px] font-mono bg-white border border-red-200 rounded p-2 max-h-32 overflow-y-auto">
                            {t.bad_rows.slice(0, 10).map((r, i) => (
                              <div key={i} className="text-slate-700 truncate">
                                #{i + 1} {Object.entries(r).filter(([k]) => k !== "id").map(([k, v]) => {
                                  const empty = v === null || v === undefined || String(v).trim() === "";
                                  return `${k}=${empty ? "∅" : v}`;
                                }).join(" · ")} <span className="text-slate-400">({(r.id || "").slice(0, 8)})</span>
                              </div>
                            ))}
                            {t.bad_rows.length > 10 && (
                              <div className="text-slate-500 italic">... και {t.bad_rows.length - 10} ακόμη</div>
                            )}
                          </div>
                        )}
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* OK tables */}
              {ok.length > 0 && (
                <div>
                  <div className="text-xs font-bold uppercase text-emerald-700 mb-1.5 flex items-center gap-1">
                    <CheckCircle2 size={13} /> Καθαροί ({ok.length})
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                    {ok.map((t) => (
                      <div key={t.name} className="text-xs px-2 py-1 rounded border border-emerald-200 bg-emerald-50 flex justify-between" data-testid={`db-health-ok-${t.name}`}>
                        <span className="truncate">{t.label}</span>
                        <span className="font-mono font-bold text-emerald-700">{t.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Info-only tables */}
              {info.length > 0 && (
                <div>
                  <div className="text-xs font-bold uppercase text-slate-500 mb-1.5 flex items-center gap-1">
                    <Info size={13} /> Πληροφοριακά (χωρίς έλεγχο)
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                    {info.map((t) => (
                      <div key={t.name} className="text-xs px-2 py-1 rounded border border-slate-200 bg-slate-50 flex justify-between text-slate-600">
                        <span className="truncate">{t.label}</span>
                        <span className="font-mono">{t.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="px-5 py-3 border-t border-slate-200 bg-slate-50 gap-2">
          <Button variant="outline" onClick={refresh} disabled={loading} data-testid="db-health-refresh">
            <RefreshCw size={14} className="mr-1.5" /> Ανανέωση
          </Button>
          <Button onClick={onClose} data-testid="db-health-close">Κλείσιμο</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
